import {
	ActionButtonDefinitionDTO,
	AvailableUserDTO,
	CurrentActionDisplayProfileDTO,
	CustomActionButtonCommandDTO,
	DeliveryPersonDTO,
	ForceCloseDayRequest,
	GetAllPosRequest,
	GetAllPosResponse,
	GetAllPriceListsByTicketIdRequest,
	GetAllPriceListsByTicketIdResponse,
	GetAvailableLanguagesRequest,
	GetAvailableLanguagesResponse,
	GetAvailableUsersRequest,
	GetAvailableUsersResponse,
	GetCustomActionButtonCommandRequest,
	GetCustomActionButtonCommandResponse,
	GetCustomActionButtonsRequest,
	GetCustomActionButtonsResponse,
	GetCustomerDisplayImageRequest,
	GetCustomerDisplayImageResponse,
	GetDeliveryPersonsRequest,
	GetDeliveryPersonsResponse,
	GetDeviceConfigurationRequest,
	GetDeviceConfigurationResponse,
	GetLicenseContainerRequest,
	GetLicenseContainerResponse,
	GetLineCancellationReasonsRequest,
	GetLineCancellationReasonsResponse,
	GetLossReasonsRequest,
	GetLossReasonsResponse,
	GetNamedDiscountAuthorizersRequest,
	GetNamedDiscountAuthorizersResponse,
	GetNamedDiscountsByUserRequest,
	GetNamedDiscountsByUserResponse,
	GetOffersRequest,
	GetOffersResponse,
	GetPendingScaleTicketsRequest,
	GetPendingScaleTicketsResponse,
	GetPointsOfSaleStatusRequest,
	GetPointsOfSaleStatusResponse,
	GetPredefinedNotesRequest,
	GetPredefinedNotesResponse,
	GetPreparationCounterLastNumberRequest,
	GetPreparationCounterLastNumberResponse,
	GetPreparationOrdersRequest,
	GetPreparationOrdersResponse,
	GetPreparationTypesRequest,
	GetPreparationTypesResponse,
	GetPriceListConfigurationRequest,
	GetPriceListConfigurationResponse,
	GetResellerBannerRequest,
	GetResellerBannerResponse,
	GetRestaurantStatusRequest,
	GetRestaurantStatusResponse,
	GetSaleCenterDefaultProductGroupRequest,
	GetSaleCenterDefaultProductGroupResponse,
	GetVatsRequest,
	GetVatsResponse,
	ImageDTO,
	LanguageDTO,
	LicenseContainerDTO,
	NamedDiscountDTO,
	NamedDiscountType,
	OfferInListDTO,
	PointOfSaleStatusDTO,
	PosInListDTO,
	PredefinedNoteDTO,
	PreparationOrderDTO,
	PreparationTypeDTO,
	PriceListConfigurationDTO,
	PriceListDTO,
	ResellerBannerDTO,
	RestaurantStatusDTO,
	ScaleTicketDTO,
	SetPreparationCounterLastNumberRequest,
	SetPriceListConfigurationRequest,
	VatDTO,
	RecipeIngredientDTO,
	GetRecipeIngredientsRequest,
	GetRecipeIngredientsResponse,
	GetTelemetrySettingsResponse,
	GetTelemetrySettingsRequest,
	GetDocumentIdByGlobalIdRequest,
	GetDocumentIdByGlobalIdResponse,
	EntityType
} from 'core/api';
import { Bus } from 'core/infrastructure';
import { Cache } from 'core/utils';

const process = Bus.buildProcessFn('/bus/');

const cache = new Cache();

export function getLicenseContainerAndVersion(): Promise<LicenseContainerDTO> {
	const request = new GetLicenseContainerRequest();
	return process(request, (response: GetLicenseContainerResponse) => response.Container);
}

export function getResellerBanner(): Promise<ResellerBannerDTO> {
	const request = new GetResellerBannerRequest();
	return process(request, (response: GetResellerBannerResponse) => response.Banner);
}

export function getAvailableUsers(): Promise<AvailableUserDTO[]> {
	const request = new GetAvailableUsersRequest();
	return process(request, (response: GetAvailableUsersResponse) => response.Users);
}

export function getPredefinedNotes(saleFormatId: number): Promise<PredefinedNoteDTO[]> {
	const request = new GetPredefinedNotesRequest({ saleFormatId });
	return process(request, (response: GetPredefinedNotesResponse) => response.Notes);
}

export function getSaleCenterDefaultProductGroup(saleCenterId: number): Promise<GetSaleCenterDefaultProductGroupResponse> {
	const request = new GetSaleCenterDefaultProductGroupRequest({ saleCenterId });
	return process(request, (response: GetSaleCenterDefaultProductGroupResponse) => response);
}

export async function getPreparationOrders(): Promise<PreparationOrderDTO[]> {
	const request = new GetPreparationOrdersRequest();
	return await process(request, (response: GetPreparationOrdersResponse) => response.Orders);
}

export async function getCancellationReasons(): Promise<string[]> {
	const request = new GetLineCancellationReasonsRequest();
	return process(request, (response: GetLineCancellationReasonsResponse) => response.Reasons);
}

export async function getNamedDiscountsByUser(ticketId: number, type: NamedDiscountType): Promise<NamedDiscountDTO[]> {
	const request = new GetNamedDiscountsByUserRequest({ type, ticketId });
	return await process(request, (response: GetNamedDiscountsByUserResponse) => response.Discounts);
}

export async function getAllPriceListsByTicketId(ticketId: number): Promise<PriceListDTO[]> {
	const request = new GetAllPriceListsByTicketIdRequest({ ticketId });
	return process(request, (response: GetAllPriceListsByTicketIdResponse) => response.PriceLists);
}

export async function getPreparationTypes(): Promise<PreparationTypeDTO[]> {
	const request = new GetPreparationTypesRequest();
	return process(request, (response: GetPreparationTypesResponse) => response.Types);
}

export async function getVats(): Promise<VatDTO[]> {
	const request = new GetVatsRequest();
	return process(request, (response: GetVatsResponse) => response.Vats);
}

export async function getOffers(ticketId: number): Promise<OfferInListDTO[]> {
	const request = new GetOffersRequest({ ticketId });
	return await process(request, (response: GetOffersResponse) => response.Offers);
}

export async function getRestaurantStatus(): Promise<RestaurantStatusDTO> {
	const request = new GetRestaurantStatusRequest();
	return process(request, (response: GetRestaurantStatusResponse) => response.Status);
}

export async function getCustomActionButtons(): Promise<ActionButtonDefinitionDTO[]> {
	return cache.getAsync('customActionButtons', () => {
		const request = new GetCustomActionButtonsRequest();
		return process(request, (response: GetCustomActionButtonsResponse) => response.Buttons);
	});
}

export async function getDisplayProfile(): Promise<CurrentActionDisplayProfileDTO> {
	const config = await getDeviceConfiguration();
	return config.DisplayProfile;
}

export function getLanguages(): Promise<LanguageDTO[]> {
	const request = new GetAvailableLanguagesRequest();
	return process(request, (response: GetAvailableLanguagesResponse) => response.Languages);
}

export function getAllPos(): Promise<PosInListDTO[]> {
	const request = new GetAllPosRequest();
	return process(request, (response: GetAllPosResponse) => response.All);
}

export async function getCustomActionButtonCommand(actionId: string): Promise<CustomActionButtonCommandDTO> {
	const request = new GetCustomActionButtonCommandRequest({ actionId });
	return process(request, (response: GetCustomActionButtonCommandResponse) => response.Command);
}

export async function getDeviceConfiguration(): Promise<GetDeviceConfigurationResponse> {
	return cache.getAsync('deviceConfiguration', () => {
		const request = new GetDeviceConfigurationRequest({ useDesktopProfile: false });
		return process(request, (response: GetDeviceConfigurationResponse) => response);
	});
}

export function getCustomerDisplayImage(imageId: string): Promise<ImageDTO> {
	const request = new GetCustomerDisplayImageRequest({ imageId });
	return process(request, (response: GetCustomerDisplayImageResponse) => response.Image);
}

export function getDeliveryPersons(): Promise<DeliveryPersonDTO[]> {
	const request = new GetDeliveryPersonsRequest();
	return process(request, (response: GetDeliveryPersonsResponse) => response.Statuses);
}

export function getPreparationCounterLastNumber(): Promise<number> {
	const request = new GetPreparationCounterLastNumberRequest();
	return process(request, (response: GetPreparationCounterLastNumberResponse) => response.LastNumber);
}

export function setPreparationCounterLastNumber(lastNumber: number): Promise<void> {
	const request = new SetPreparationCounterLastNumberRequest({ lastNumber });
	return process(request);
}

export function getPointsOfSaleStatus(): Promise<PointOfSaleStatusDTO[]> {
	const request = new GetPointsOfSaleStatusRequest();
	return process(request, (response: GetPointsOfSaleStatusResponse) => response.PointsOfSale);
}

export function foreCloseDay(posIds: number[]): Promise<void> {
	const request = new ForceCloseDayRequest({ posIds });
	return process(request);
}

export function getPriceListConfiguration(): Promise<PriceListConfigurationDTO> {
	const request = new GetPriceListConfigurationRequest();
	return process(request, (response: GetPriceListConfigurationResponse) => response.Configuration);
}

export function setPriceListConfiguration(configuration: PriceListConfigurationDTO): Promise<void> {
	const request = new SetPriceListConfigurationRequest({ configuration });
	return process(request);
}

export function getNamedDiscountAuthorizers(discountId: number): Promise<AvailableUserDTO[]> {
	const request = new GetNamedDiscountAuthorizersRequest({ discountId });
	return process(request, (response: GetNamedDiscountAuthorizersResponse) => response.Users);
}

export function getNamedDiscounts(type: NamedDiscountType, ticketId: number): Promise<NamedDiscountDTO[]> {
	const request = new GetNamedDiscountsByUserRequest({ type, ticketId });
	return process(request, (response: GetNamedDiscountsByUserResponse) => response.Discounts);
}

export function getLossReasons(): Promise<string[]> {
	const request = new GetLossReasonsRequest();
	return process(request, (response: GetLossReasonsResponse) => response.Reasons);
}

export function getRecipeIngredients(saleFormatId: number): Promise<RecipeIngredientDTO[]> {
	const request = new GetRecipeIngredientsRequest({ saleFormatId });
	return process(request, (response: GetRecipeIngredientsResponse) => response.Ingredients);
}

export function getPendingScaleTickets(ticketId: number): Promise<ScaleTicketDTO[]> {
	const request = new GetPendingScaleTicketsRequest({ ticketId });
	return process(request, (response: GetPendingScaleTicketsResponse) => response.Tickets);
}

export function getTelemetrySettings(): Promise<GetTelemetrySettingsResponse> {
	const request = new GetTelemetrySettingsRequest();
	return process(request, (response: GetTelemetrySettingsResponse) => response);
}

export function getDocumentIdByGlobalId(globalId: string, type: EntityType): Promise<number> {
	const request = new GetDocumentIdByGlobalIdRequest({ globalId, type });
	return process(request, (response: GetDocumentIdByGlobalIdResponse) => response.Id);
}
